'use client';

// ── Profit Target · Challenge — "$50,000 account" chart ───────────────────────
// Figma: Moldura 12063:48794 (desktop, 792×468) and 12469:1805 (mobile, 366×585).
// Every number is an illustrative example (the page note says so); geometry is
// Figma px / 8 in rem. The chart vectors are the Figma exports in
// /public/general-rules-v3/profit-target/, placed at their Figma boxes
// (stroke bleed already included in the files, hence the fractional offsets).

import { ExampleLabel, FigureFrame } from '../primitives';

const ASSETS = '/general-rules-v3/profit-target';

const TEXT = 'mb-0 whitespace-nowrap text-[2rem] leading-[3rem]';
const AXIS = `${TEXT} absolute font-normal text-black/40`;

function Kpi({ label, value, valueClassName }: { label: string; value: string; valueClassName: string }) {
  return (
    <div className="flex flex-col gap-[0.5rem]">
      <p className={`${TEXT} font-normal text-black/[0.64]`}>{label}</p>
      <p className={`mb-0 whitespace-nowrap text-[4rem] font-medium leading-[5rem] ${valueClassName}`}>{value}</p>
    </div>
  );
}

function AxisPill({ children, className, tone }: { children: string; className: string; tone: 'green' | 'dark' }) {
  return (
    <div
      className={`absolute right-0 rounded-[0.25rem] px-[0.75rem] py-[0.25rem] ${tone === 'green' ? 'bg-[#228e71]' : 'bg-[#001b14]'} ${className}`}
    >
      <p className={`${TEXT} font-medium text-white`}>{children}</p>
    </div>
  );
}

function Tooltip({ className }: { className: string }) {
  return (
    <div
      data-ui="figure.profitTargetChallenge.tooltip"
      className={`absolute flex flex-col gap-[0.25rem] rounded-[0.25rem] bg-[#001b14] ${className}`}
    >
      <p className={`${TEXT} font-normal text-white/[0.64]`}>Day 7 · close</p>
      <p className={`${TEXT} font-medium text-[#1aa87f]`}>+$2,100 · +4.2%</p>
    </div>
  );
}

/** Desktop chart · Figma 11877:1079 (744×280). */
function ChartDesktop() {
  return (
    <div data-ui="figure.profitTargetChallenge.chart" className="relative hidden h-[35rem] w-full lg:block">
      {/* grid */}
      <div className="absolute left-[0.375rem] top-[5.375rem] h-px w-[82.235rem] bg-black/[0.06]" />
      <div className="absolute left-[0.375rem] top-[13.25rem] h-px w-[82.235rem] bg-black/[0.06]" />
      <div className="absolute left-[0.375rem] top-[21.25rem] h-px w-[82.235rem] bg-black/[0.06]" />
      <div className="absolute left-[0.375rem] top-[29.125rem] h-px w-[82.235rem] bg-black/[0.06]" />

      {/* area + line + target */}
      <img alt="" src={`${ASSETS}/challenge-area.svg`} className="absolute left-[0.375rem] top-[12.5rem] h-[19rem] w-[82.235rem] max-w-none" />
      <img alt="" src={`${ASSETS}/challenge-line.svg`} className="absolute left-[0.28125rem] top-[12.40625rem] h-[16.8125rem] w-[82.4224rem] max-w-none" />
      <img alt="" src={`${ASSETS}/challenge-target.svg`} className="absolute left-[0.375rem] top-[5.3125rem] h-[0.125rem] w-[82.235rem] max-w-none" />
      <p className={`${TEXT} absolute left-[0.8275rem] top-[2.125rem] font-medium text-[#228e71]`}>Profit target</p>

      {/* right axis */}
      <AxisPill tone="green" className="top-[3.875rem]">$53,000</AxisPill>
      <AxisPill tone="dark" className="top-[11rem]">$52,100</AxisPill>
      <p className={`${AXIS} right-0 top-[19.75rem]`}>$51,000</p>
      <p className={`${AXIS} right-0 top-[27.625rem]`}>$50,000</p>

      {/* markers */}
      <img alt="" src={`${ASSETS}/challenge-dot-start.svg`} className="absolute left-[-0.1875rem] top-[28.5625rem] h-[1.125rem] w-[1.125rem] max-w-none" />
      <img alt="" src={`${ASSETS}/challenge-ruler.svg`} data-ui="figure.profitTargetChallenge.ruler" className="absolute left-[78.755rem] top-[5.6875rem] h-[5.375rem] w-[1rem] max-w-none" />
      <p data-ui="figure.profitTargetChallenge.to-go" className={`${TEXT} absolute left-[67.134rem] top-[6.875rem] font-medium text-[#ac0003]`}>$900 to go</p>
      <img alt="" src={`${ASSETS}/challenge-crosshair.svg`} className="absolute left-[82.5475rem] top-[3rem] h-[28.5rem] w-[0.125rem] max-w-none" />
      <img alt="" src={`${ASSETS}/challenge-halo.svg`} className="absolute left-[81.376rem] top-[11.25rem] h-[2.5rem] w-[2.5rem] max-w-none" />
      <img alt="" src={`${ASSETS}/challenge-dot-now.svg`} className="absolute left-[82.001rem] top-[11.875rem] h-[1.25rem] w-[1.25rem] max-w-none" />
      <Tooltip className="left-[60.5rem] top-[22.75rem] px-[1.5rem] py-[1.25rem]" />

      {/* x axis */}
      <p className={`${AXIS} left-[0.5575rem] top-[32rem]`}>Start</p>
      <p className={`${AXIS} left-[10.005rem] top-[32rem]`}>Day 1</p>
      <p className={`${AXIS} left-[21.486rem] top-[32rem]`}>Day 2</p>
      <p className={`${AXIS} left-[33.3525rem] top-[32rem]`}>Day 3</p>
      <p className={`${AXIS} left-[45.0825rem] top-[32rem]`}>Day 4</p>
      <p className={`${AXIS} left-[56.679rem] top-[32rem]`}>Day 5</p>
      <p className={`${AXIS} left-[68.545rem] top-[32rem]`}>Day 6</p>
      <p className={`${AXIS} left-[80.401rem] top-[32rem]`}>Day 7</p>
    </div>
  );
}

/** Mobile chart · Figma 12471:1993 (318×337). */
function ChartMobile() {
  return (
    <div data-ui="figure.profitTargetChallenge.chart-mobile" className="relative h-[42.125rem] w-full lg:hidden">
      {/* grid */}
      <div className="absolute left-[0.375rem] top-[5.394rem] h-px w-[27.125rem] bg-black/[0.06]" />
      <div className="absolute left-[0.375rem] top-[15.631rem] h-px w-[27.125rem] bg-black/[0.06]" />
      <div className="absolute left-[0.375rem] top-[26.031rem] h-px w-[27.125rem] bg-black/[0.06]" />
      <div className="absolute left-[0.375rem] top-[36.269rem] h-px w-[27.125rem] bg-black/[0.06]" />

      {/* area + line + target */}
      <img alt="" src={`${ASSETS}/challenge-area-mobile.svg`} className="absolute left-[0.375rem] top-[14.6375rem] h-[24.7rem] w-[27.12rem] max-w-none" />
      <img alt="" src={`${ASSETS}/challenge-line-mobile.svg`} className="absolute left-[0.28rem] top-[14.544rem] h-[21.8rem] w-[27.307rem] max-w-none" />
      <img alt="" src={`${ASSETS}/challenge-target-mobile.svg`} className="absolute left-[0.375rem] top-[5.3125rem] h-[0.125rem] w-[27.12rem] max-w-none" />
      <p className={`${TEXT} absolute left-[0.741rem] top-[2.125rem] font-medium text-[#228e71]`}>Profit target</p>

      {/* right axis */}
      <AxisPill tone="green" className="top-[3.95rem]">$53,000</AxisPill>
      <AxisPill tone="dark" className="top-[13.2125rem]">$52,100</AxisPill>
      <p className={`${AXIS} right-0 top-[24.5125rem]`}>$51,000</p>
      <p className={`${AXIS} right-0 top-[34.75rem]`}>$50,000</p>

      {/* markers */}
      <img alt="" src={`${ASSETS}/challenge-dot-start.svg`} className="absolute left-[-0.1875rem] top-[35.6875rem] h-[1.125rem] w-[1.125rem] max-w-none" />
      <img alt="" src={`${ASSETS}/challenge-ruler-mobile.svg`} data-ui="figure.profitTargetChallenge.ruler-mobile" className="absolute left-[25.936rem] top-[5.8rem] h-[6.95rem] w-[1rem] max-w-none" />
      <p data-ui="figure.profitTargetChallenge.to-go-mobile" className={`${TEXT} absolute left-[13.8775rem] top-[7.775rem] font-medium text-[#ac0003]`}>$900 to go</p>
      <img alt="" src={`${ASSETS}/challenge-crosshair-mobile.svg`} className="absolute left-[27.4375rem] top-[3rem] h-[37.05rem] w-[0.125rem] max-w-none" />
      <img alt="" src={`${ASSETS}/challenge-halo.svg`} className="absolute left-[26.25rem] top-[13.3875rem] h-[2.5rem] w-[2.5rem] max-w-none" />
      <img alt="" src={`${ASSETS}/challenge-dot-now.svg`} className="absolute left-[26.875rem] top-[14.0125rem] h-[1.25rem] w-[1.25rem] max-w-none" />
      <Tooltip className="left-0 top-[11.5rem] px-[1.25rem] py-[1rem]" />

      {/* x axis */}
      <p className={`${AXIS} left-[0.5225rem] top-[39.125rem]`}>Start</p>
      <p className={`${AXIS} left-[9.455rem] top-[39.125rem]`}>Day 3</p>
      <p className={`${AXIS} left-[17.1675rem] top-[39.125rem]`}>Day 5</p>
      <p className={`${AXIS} left-[25.004rem] top-[39.125rem]`}>Day 7</p>
    </div>
  );
}

export default function ProfitTargetChallenge() {
  return (
    <FigureFrame data-ui="figure.profitTargetChallenge" className="font-geist">
      <ExampleLabel>EXAMPLE · $50,000 ACCOUNT</ExampleLabel>

      {/* Números: KPI pair + progress */}
      <div
        data-ui="figure.profitTargetChallenge.numbers"
        className="flex w-full flex-col gap-[3rem] lg:flex-row lg:items-start lg:justify-between"
      >
        <div className="flex gap-[6rem]">
          <Kpi label="Balance" value="$52,100" valueClassName="text-black" />
          <Kpi label="Profit target" value="$53,000" valueClassName="text-[#228e71]" />
        </div>
        <div data-ui="figure.profitTargetChallenge.progress" className="flex w-full flex-col gap-[1rem] lg:w-[30rem]">
          <div className="flex w-full items-start justify-between">
            <p className={`${TEXT} font-normal text-black/[0.64]`}>Progress</p>
            <p className={`${TEXT} font-medium text-black`}>70%</p>
          </div>
          <div className="relative h-[0.5rem] w-full overflow-hidden rounded-[0.25rem] bg-black/[0.08]">
            <div className="absolute left-0 top-0 h-[0.5rem] w-[70%] bg-[#228e71]" />
          </div>
        </div>
      </div>

      <ChartDesktop />
      <ChartMobile />
    </FigureFrame>
  );
}
